import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Send, Square, Trash2, X } from "lucide-react";
import "./voice.css";

type Props = {
  disabled?: boolean;
  onSend: (file: File) => Promise<void>;
  onError: (message: string) => void;
};
const format = (seconds: number) =>
  `${Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;
export function VoiceRecorder({ disabled, onSend, onError }: Props) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const stream = useRef<MediaStream | null>(null);
  const previewUrl = useMemo(
    () => (blob ? URL.createObjectURL(blob) : null),
    [blob],
  );
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setSeconds((v) => v + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);
  useEffect(
    () => () => {
      stream.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );
  const start = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder)
        throw new Error("Voice notes are not supported by this browser.");
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : undefined;
      const next = new MediaRecorder(
        stream.current,
        mime ? { mimeType: mime } : undefined,
      );
      chunks.current = [];
      next.ondataavailable = (e) => {
        if (e.data.size) chunks.current.push(e.data);
      };
      next.onstop = () => {
        const audio = new Blob(chunks.current, {
          type: next.mimeType || "audio/webm",
        });
        setBlob(audio);
        stream.current?.getTracks().forEach((track) => track.stop());
        stream.current = null;
      };
      recorder.current = next;
      setSeconds(0);
      setBlob(null);
      next.start();
      setRecording(true);
    } catch (error) {
      onError(
        error instanceof Error ? error.message : "Could not start recording.",
      );
    }
  };
  const stop = () => {
    if (recorder.current?.state === "recording") recorder.current.stop();
    setRecording(false);
  };
  const cancel = () => {
    setBlob(null);
    setSeconds(0);
    if (recording) stop();
  };
  const send = async () => {
    if (!blob) return;
    try {
      await onSend(
        new File([blob], `voice-note-${Date.now()}.webm`, {
          type: blob.type || "audio/webm",
        }),
      );
      setBlob(null);
      setSeconds(0);
    } catch (error) {
      onError(
        error instanceof Error ? error.message : "Voice note upload failed.",
      );
    }
  };
  if (recording)
    return (
      <div className="voiceRecording">
        <span className="recordDot" /> <b>{format(seconds)}</b>
        <button onClick={stop} aria-label="Stop recording">
          <Square />
        </button>
        <button onClick={cancel} aria-label="Cancel recording">
          <X />
        </button>
      </div>
    );
  if (blob)
    return (
      <div className="voicePreview">
        <audio controls src={previewUrl ?? undefined} />
        <button onClick={cancel} aria-label="Delete voice note">
          <Trash2 />
        </button>
        <button
          className="round"
          onClick={() => void send()}
          aria-label="Send voice note"
        >
          <Send />
        </button>
      </div>
    );
  return (
    <button
      disabled={disabled}
      onClick={() => void start()}
      aria-label="Record voice note"
    >
      <Mic />
    </button>
  );
}
